import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Calculator,
  Car,
  FileText,
  Euro,
  Phone,
  CheckCircle,
  Info,
  ChevronRight,
  Ship,
  Globe,
  Camera,
  Shield,
  Clock,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// French departments list
const DEPARTMENTS = [
  { code: "75", name: "Paris (75)" },
  { code: "77", name: "Seine-et-Marne (77)" },
  { code: "78", name: "Yvelines (78)" },
  { code: "91", name: "Essonne (91)" },
  { code: "92", name: "Hauts-de-Seine (92)" },
  { code: "93", name: "Seine-Saint-Denis (93)" },
  { code: "94", name: "Val-de-Marne (94)" },
  { code: "95", name: "Val-d'Oise (95)" },
  { code: "13", name: "Bouches-du-Rhône (13)" },
  { code: "31", name: "Haute-Garonne (31)" },
  { code: "33", name: "Gironde (33)" },
  { code: "34", name: "Hérault (34)" },
  { code: "44", name: "Loire-Atlantique (44)" },
  { code: "59", name: "Nord (59)" },
  { code: "67", name: "Bas-Rhin (67)" },
  { code: "69", name: "Rhône (69)" },
  { code: "other", name: "Autre département" },
];

const CalculatorPage = () => {
  const [packs, setPacks] = useState([]);
  const [activeTab, setActiveTab] = useState("europe");
  
  // Europe form state
  const [europeForm, setEuropeForm] = useState({
    vehiclePrice: "",
    co2Emissions: "",
    fiscalPower: "",
    department: "",
    isNewVehicle: false,
    vehicleAge: "0",
    selectedPack: "",
  });
  const [europeResults, setEuropeResults] = useState(null);
  
  // Korea form state
  const [koreaForm, setKoreaForm] = useState({
    vehiclePrice: "",
    currency: "EUR",
    containerPrice: "2500",
  });
  const [koreaResults, setKoreaResults] = useState(null);
  const [koreaInfo, setKoreaInfo] = useState(null);
  
  // Callback form
  const [callbackForm, setCallbackForm] = useState({
    name: "",
    phone: "",
    email: "",
    preferredTime: "",
    timeline: "",
    financingType: "",
    budgetConfirmed: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [callbackSent, setCallbackSent] = useState(false);
  const [showCallback, setShowCallback] = useState(false);

  useEffect(() => {
    // SEO title
    document.title = "Simulateur d'Importation | Fast&Car - Estimez votre projet";
    
    const fetchData = async () => {
      try {
        const [packsRes, koreaInfoRes] = await Promise.all([
          axios.get(`${API}/packs`),
          axios.get(`${API}/korea-import-info`)
        ]);
        setPacks(packsRes.data);
        setKoreaInfo(koreaInfoRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const calculateEurope = async () => {
    if (!europeForm.vehiclePrice || !europeForm.fiscalPower || !europeForm.department || !europeForm.selectedPack) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      const regResponse = await axios.post(`${API}/calculate-registration`, {
        vehicle_price: parseFloat(europeForm.vehiclePrice),
        co2_emissions: parseInt(europeForm.co2Emissions) || 0,
        fiscal_power: parseInt(europeForm.fiscalPower),
        department: europeForm.department,
        is_new: europeForm.isNewVehicle,
        vehicle_age_years: parseInt(europeForm.vehicleAge) || 0,
      });

      const selectedPackData = packs.find(p => p.name === europeForm.selectedPack);
      const packPrice = selectedPackData?.price_value || 0;
      const vehiclePrice = parseFloat(europeForm.vehiclePrice);
      const registrationCost = regResponse.data.total_registration;
      const grandTotal = vehiclePrice + packPrice + registrationCost;

      setEuropeResults({
        vehiclePrice,
        packPrice,
        packName: europeForm.selectedPack,
        registration: regResponse.data,
        grandTotal,
      });
      setShowCallback(true);

      // Track simulation
      try {
        await axios.post(`${API}/analytics/simulation`, {
          simulation_type: "europe",
          vehicle_price: vehiclePrice,
          total_cost: grandTotal,
        });
      } catch (e) {
        console.debug("Analytics tracking failed:", e);
      }
    } catch (error) {
      console.error("Error calculating:", error);
      toast.error("Erreur lors du calcul");
    }
  };

  const calculateKorea = async () => {
    if (!koreaForm.vehiclePrice) {
      toast.error("Veuillez entrer le prix du véhicule");
      return;
    }

    try {
      const response = await axios.post(`${API}/calculate-korea-import`, {
        vehicle_price: parseFloat(koreaForm.vehiclePrice),
        currency: koreaForm.currency,
        container_price: parseFloat(koreaForm.containerPrice) || 2500,
      });

      setKoreaResults(response.data);
      setShowCallback(true);

      // Track simulation
      try {
        await axios.post(`${API}/analytics/simulation`, {
          simulation_type: "korea",
          vehicle_price: parseFloat(koreaForm.vehiclePrice),
          total_cost: response.data.total_estimate,
        });
      } catch (e) {
        console.debug("Analytics tracking failed:", e);
      }
    } catch (error) {
      console.error("Error calculating Korea import:", error);
      toast.error("Erreur lors du calcul");
    }
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const currentResults = activeTab === "europe" ? europeResults : koreaResults;
      await axios.post(`${API}/callback`, {
        name: callbackForm.name,
        phone: callbackForm.phone,
        email: callbackForm.email || null,
        preferred_time: callbackForm.preferredTime || null,
        vehicle_price: currentResults?.vehiclePrice || currentResults?.vehicle_price_eur,
        pack_selected: activeTab === "europe" ? europeResults?.packName : "Import Corée",
        estimated_total: currentResults?.grandTotal || currentResults?.total_cost,
        project_type: activeTab,
        timeline: callbackForm.timeline,
        budget_confirmed: callbackForm.budgetConfirmed,
        financing_type: callbackForm.financingType,
      });
      setCallbackSent(true);
      toast.success("Demande de rappel envoyée !");
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const resetAll = () => {
    setEuropeResults(null);
    setKoreaResults(null);
    setShowCallback(false);
    setCallbackSent(false);
  };

  return (
    <div data-testid="calculator-page" className="pt-32 md:pt-40">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-carbon-paper border-b border-zinc-800 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20 pointer-events-none" 
          style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} 
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
              Simulateur
            </p>
            <h1
              className="font-unbounded text-4xl md:text-6xl font-black text-white leading-tight mb-6"
              data-testid="calculator-title"
            >
              Estimez votre
              <br />
              <span className="text-neon-lime text-glow">import</span>
            </h1>
            <p className="font-manrope text-lg md:text-xl text-zinc-400 leading-relaxed">
              Calculez le coût total de votre projet selon la provenance : 
              Europe (avec carte grise et malus 2026) ou Corée du Sud (avec droits de douane et TVA).
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 md:py-24 relative">
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none" 
          style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} 
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); resetAll(); }} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 bg-zinc-900 border border-zinc-800 p-1 rounded-none h-auto">
              <TabsTrigger 
                value="europe" 
                className="py-4 px-6 font-unbounded uppercase tracking-wider text-sm data-[state=active]:bg-neon-lime data-[state=active]:text-black rounded-none"
                data-testid="tab-europe"
              >
                <Globe className="w-4 h-4 mr-2" />
                Europe
              </TabsTrigger>
              <TabsTrigger 
                value="korea" 
                className="py-4 px-6 font-unbounded uppercase tracking-wider text-sm data-[state=active]:bg-neon-lime data-[state=active]:text-black rounded-none"
                data-testid="tab-korea"
              >
                <Ship className="w-4 h-4 mr-2" />
                Corée du Sud
              </TabsTrigger>
            </TabsList>

            {/* Europe Tab */}
            <TabsContent value="europe">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Europe Form */}
                <div className="bg-zinc-900/50 border border-zinc-800 p-8 backdrop-blur-sm" data-testid="europe-form">
                  <h2 className="font-unbounded text-2xl font-bold text-white mb-8 flex items-center gap-3">
                    <Calculator className="w-6 h-6 text-neon-lime" />
                    Import Europe
                  </h2>

                  <div className="space-y-6">
                    {/* Vehicle Price */}
                    <div className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        <Euro className="w-4 h-4 text-neon-lime" />
                        Prix du véhicule repéré *
                      </Label>
                      <Input
                        type="number"
                        placeholder="Ex: 35000"
                        value={europeForm.vehiclePrice}
                        onChange={(e) => setEuropeForm({...europeForm, vehiclePrice: e.target.value})}
                        data-testid="input-vehicle-price"
                        className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none"
                      />
                    </div>

                    {/* Pack Selection */}
                    <div className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        <FileText className="w-4 h-4 text-neon-lime" />
                        Forfait Fast&Car *
                      </Label>
                      <Select
                        value={europeForm.selectedPack}
                        onValueChange={(value) => setEuropeForm({...europeForm, selectedPack: value})}
                      >
                        <SelectTrigger data-testid="select-pack" className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none">
                          <SelectValue placeholder="Choisir un forfait" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {packs.map((pack) => (
                            <SelectItem key={pack.name} value={pack.name}>
                              {pack.name} - {pack.price}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Fiscal Power */}
                    <div className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        <Car className="w-4 h-4 text-neon-lime" />
                        Puissance fiscale (CV) *
                      </Label>
                      <Input
                        type="number"
                        placeholder="Ex: 7"
                        value={europeForm.fiscalPower}
                        onChange={(e) => setEuropeForm({...europeForm, fiscalPower: e.target.value})}
                        data-testid="input-fiscal-power"
                        className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none"
                      />
                    </div>

                    {/* CO2 Emissions */}
                    <div className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        Émissions CO2 (g/km)
                        <span className="text-zinc-500 text-xs">(malus 2026)</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="Ex: 125"
                        value={europeForm.co2Emissions}
                        onChange={(e) => setEuropeForm({...europeForm, co2Emissions: e.target.value})}
                        data-testid="input-co2"
                        className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none"
                      />
                    </div>

                    {/* Department */}
                    <div className="space-y-2">
                      <Label className="text-white">Département d'immatriculation *</Label>
                      <Select
                        value={europeForm.department}
                        onValueChange={(value) => setEuropeForm({...europeForm, department: value})}
                      >
                        <SelectTrigger data-testid="select-department" className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                          {DEPARTMENTS.map((dept) => (
                            <SelectItem key={dept.code} value={dept.code}>
                              {dept.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* New Vehicle */}
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={europeForm.isNewVehicle}
                        onCheckedChange={(checked) => setEuropeForm({...europeForm, isNewVehicle: checked})}
                        data-testid="checkbox-new"
                        className="border-zinc-600 data-[state=checked]:bg-neon-lime data-[state=checked]:border-neon-lime"
                      />
                      <Label className="text-white">Véhicule neuf ou moins de 6 mois</Label>
                    </div>

                    {/* Vehicle Age */}
                    {!europeForm.isNewVehicle && (
                      <div className="space-y-2">
                        <Label className="text-white">Âge du véhicule</Label>
                        <Select
                          value={europeForm.vehicleAge}
                          onValueChange={(value) => setEuropeForm({...europeForm, vehicleAge: value})}
                        >
                          <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {[...Array(20)].map((_, i) => (
                              <SelectItem key={i} value={String(i)}>
                                {i === 0 ? "Moins d'1 an" : `${i} an${i > 1 ? 's' : ''}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <Button
                      onClick={calculateEurope}
                      data-testid="calculate-europe-btn"
                      className="w-full bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider py-6 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
                    >
                      <span className="flex items-center gap-2">
                        Calculer
                        <ChevronRight className="w-5 h-5" />
                      </span>
                    </Button>
                  </div>
                </div>

                {/* Europe Results */}
                <div>
                  {europeResults ? (
                    <div className="bg-zinc-900/50 border border-zinc-800 p-8 sticky top-24 backdrop-blur-sm" data-testid="europe-results">
                      <h2 className="font-unbounded text-2xl font-bold text-white mb-8">
                        Estimation Import Europe
                      </h2>

                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-3 border-b border-zinc-800">
                          <span className="text-zinc-400">Prix du véhicule</span>
                          <span className="font-mono text-white text-lg">{formatPrice(europeResults.vehiclePrice)}</span>
                        </div>

                        <div className="flex justify-between items-center py-3 border-b border-zinc-800">
                          <span className="text-zinc-400">{europeResults.packName}</span>
                          <span className="font-mono text-white text-lg">{formatPrice(europeResults.packPrice)}</span>
                        </div>

                        <div className="py-3 border-b border-zinc-800">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-zinc-400">Carte grise (estimation)</span>
                            <span className="font-mono text-white text-lg">{formatPrice(europeResults.registration.total_registration)}</span>
                          </div>
                          <div className="pl-4 space-y-1 text-sm">
                            <div className="flex justify-between text-zinc-500">
                              <span>Taxe régionale ({europeResults.registration.details.fiscal_power} CV)</span>
                              <span>{formatPrice(europeResults.registration.regional_tax)}</span>
                            </div>
                            {europeResults.registration.co2_malus > 0 && (
                              <div className="flex justify-between text-red-400">
                                <span>Malus CO2 2026</span>
                                <span>{formatPrice(europeResults.registration.co2_malus)}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="calculator-result py-6 px-4 mt-6">
                          <div className="flex justify-between items-center">
                            <span className="font-unbounded text-white font-bold text-lg">TOTAL ESTIMÉ</span>
                            <span className="font-unbounded text-neon-lime text-3xl font-black text-glow">
                              {formatPrice(europeResults.grandTotal)}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-start gap-2 mt-4 p-4 bg-zinc-800/50 text-xs text-zinc-500">
                          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <p>
                            <strong className="text-zinc-400">Ces montants sont donnés à titre indicatif</strong> et sont susceptibles de différer en fonction de plusieurs facteurs, notamment les coûts de transport et la réglementation fiscale en constante évolution.{" "}
                            <a 
                              href="https://www.service-public.fr/simulateur/calcul/cout-certificat-immatriculation" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-neon-lime hover:underline"
                            >
                              Simulateur officiel
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-zinc-900/30 border border-zinc-800/50 border-dashed p-12 text-center">
                      <Globe className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-600">Remplissez le formulaire pour estimer votre import Europe.</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Korea Tab */}
            <TabsContent value="korea">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Korea Form */}
                <div className="space-y-8">
                  <div className="bg-zinc-900/50 border border-zinc-800 p-8 backdrop-blur-sm" data-testid="korea-form">
                    <h2 className="font-unbounded text-2xl font-bold text-white mb-8 flex items-center gap-3">
                      <Ship className="w-6 h-6 text-neon-lime" />
                      Import Corée du Sud
                    </h2>

                    <div className="space-y-6">
                      {/* Vehicle Price */}
                      <div className="space-y-2">
                        <Label className="text-white flex items-center gap-2">
                          <Euro className="w-4 h-4 text-neon-lime" />
                          Prix du véhicule en Corée *
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            placeholder="Ex: 25000"
                            value={koreaForm.vehiclePrice}
                            onChange={(e) => setKoreaForm({...koreaForm, vehiclePrice: e.target.value})}
                            data-testid="input-korea-price"
                            className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none flex-1"
                          />
                          <Select
                            value={koreaForm.currency}
                            onValueChange={(value) => setKoreaForm({...koreaForm, currency: value})}
                          >
                            <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              <SelectItem value="EUR">EUR</SelectItem>
                              <SelectItem value="KRW">KRW</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Container Price */}
                      <div className="space-y-2">
                        <Label className="text-white flex items-center gap-2">
                          <Ship className="w-4 h-4 text-neon-lime" />
                          Transport maritime (conteneur 20')
                        </Label>
                        <Input
                          type="number"
                          placeholder="2500"
                          value={koreaForm.containerPrice}
                          onChange={(e) => setKoreaForm({...koreaForm, containerPrice: e.target.value})}
                          data-testid="input-container-price"
                          className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none"
                        />
                        <p className="text-zinc-500 text-xs">Incheon → Le Havre (estimation ~2500€)</p>
                      </div>

                      <Button
                        onClick={calculateKorea}
                        data-testid="calculate-korea-btn"
                        className="w-full bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider py-6 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
                      >
                        <span className="flex items-center gap-2">
                          Calculer
                          <ChevronRight className="w-5 h-5" />
                        </span>
                      </Button>
                    </div>
                  </div>

                  {/* Korea Info */}
                  {koreaInfo && (
                    <div className="space-y-6">
                      {/* Why Korea Section */}
                      {koreaInfo.why_korea && (
                        <div className="bg-zinc-900/50 border border-neon-lime/30 p-8 backdrop-blur-sm">
                          <h3 className="font-unbounded text-xl font-bold text-white mb-6 flex items-center gap-2">
                            <span className="text-2xl">🇰🇷</span>
                            {koreaInfo.why_korea.title}
                          </h3>
                          <div className="space-y-4">
                            {koreaInfo.why_korea.reasons.map((reason, index) => (
                              <div key={index} className="border-l-2 border-neon-lime/50 pl-4">
                                <h4 className="text-neon-lime font-bold mb-1">{reason.title}</h4>
                                <p className="text-zinc-400 text-sm">{reason.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Conditions */}
                      <div className="bg-zinc-900/50 border border-zinc-800 p-8 backdrop-blur-sm">
                        <h3 className="font-unbounded text-lg font-bold text-white mb-6 flex items-center gap-2">
                          <Info className="w-5 h-5 text-neon-lime" />
                          Conditions Import Corée
                        </h3>
                        <ul className="space-y-4">
                          <li className="flex items-start gap-3 text-zinc-400">
                            <Camera className="w-5 h-5 text-neon-lime flex-shrink-0 mt-0.5" />
                            <span>Inspection sur place par notre équipe locale + dossier photo/vidéo complet</span>
                          </li>
                          <li className="flex items-start gap-3 text-zinc-400">
                            <Shield className="w-5 h-5 text-neon-lime flex-shrink-0 mt-0.5" />
                            <span>Commande ferme uniquement - Achat comptant requis avant transport</span>
                          </li>
                          <li className="flex items-start gap-3 text-zinc-400">
                            <FileText className="w-5 h-5 text-neon-lime flex-shrink-0 mt-0.5" />
                            <span>Livraison avec immatriculation provisoire française</span>
                          </li>
                          <li className="flex items-start gap-3 text-zinc-400">
                            <Clock className="w-5 h-5 text-neon-lime flex-shrink-0 mt-0.5" />
                            <span>Délai : 4-6 semaines (transit maritime)</span>
                          </li>
                        </ul>
                        <div className="mt-6 p-4 bg-zinc-800/50 border-l-2 border-neon-lime">
                          <p className="text-zinc-300 text-sm">
                            <strong className="text-white">Homologation & immatriculation définitive</strong> : 
                            démarches possibles en supplément sur devis après réception du certificat de conformité.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Korea Results */}
                <div>
                  {koreaResults ? (
                    <div className="bg-zinc-900/50 border border-zinc-800 p-8 sticky top-24 backdrop-blur-sm" data-testid="korea-results">
                      <h2 className="font-unbounded text-2xl font-bold text-white mb-8">
                        Estimation Import Corée
                      </h2>

                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-3 border-b border-zinc-800">
                          <span className="text-zinc-400">Prix du véhicule</span>
                          <span className="font-mono text-white text-lg">{formatPrice(koreaResults.vehicle_price_eur)}</span>
                        </div>

                        <div className="flex justify-between items-center py-3 border-b border-zinc-800">
                          <span className="text-zinc-400">Transport maritime (conteneur 20')</span>
                          <span className="font-mono text-white text-lg">{formatPrice(koreaResults.container_shipping)}</span>
                        </div>

                        <div className="flex justify-between items-center py-3 border-b border-zinc-800">
                          <span className="text-zinc-400">Droits de douane (10%)</span>
                          <span className="font-mono text-white text-lg">{formatPrice(koreaResults.customs_duty)}</span>
                        </div>

                        <div className="flex justify-between items-center py-3 border-b border-zinc-800">
                          <span className="text-zinc-400">TVA (20%)</span>
                          <span className="font-mono text-white text-lg">{formatPrice(koreaResults.vat)}</span>
                        </div>

                        <div className="flex justify-between items-center py-3 border-b border-zinc-800">
                          <span className="text-zinc-400">Frais annexes</span>
                          <span className="font-mono text-white text-lg">{formatPrice(koreaResults.ancillary_fees)}</span>
                        </div>
                        <p className="text-zinc-600 text-xs pl-2">Dépotage, contrôle technique, immatriculation provisoire</p>

                        <div className="py-3 border-b border-zinc-800">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-zinc-400">Forfait Fast&Car</span>
                            <span className="font-mono text-white text-lg">{formatPrice(koreaResults.fastcar_fee)}</span>
                          </div>
                          <div className="pl-4 space-y-1 text-sm">
                            <div className="flex justify-between text-zinc-500">
                              <span>Acompte à la signature</span>
                              <span>{formatPrice(koreaResults.fastcar_deposit)}</span>
                            </div>
                            <div className="flex justify-between text-zinc-500">
                              <span>Solde avant expédition</span>
                              <span>{formatPrice(koreaResults.fastcar_balance)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="calculator-result py-6 px-4 mt-6">
                          <div className="flex justify-between items-center">
                            <span className="font-unbounded text-white font-bold text-lg">TOTAL ESTIMÉ</span>
                            <span className="font-unbounded text-neon-lime text-3xl font-black text-glow">
                              {formatPrice(koreaResults.total_cost)}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-start gap-2 mt-4 p-4 bg-zinc-800/50 text-xs text-zinc-500">
                          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <p>
                            <strong className="text-zinc-400">Ces montants sont donnés à titre indicatif</strong> et sont susceptibles de différer en fonction de plusieurs facteurs, notamment les coûts de transport et la réglementation fiscale en constante évolution. Hors homologation et immatriculation définitive (sur devis).
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-zinc-900/30 border border-zinc-800/50 border-dashed p-12 text-center">
                      <Ship className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-600">Remplissez le formulaire pour estimer votre import Corée.</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Callback Form with Qualification Questions */}
          {showCallback && !callbackSent && (
            <div className="mt-16 max-w-2xl mx-auto bg-zinc-900/50 border border-zinc-800 p-8 backdrop-blur-sm" data-testid="callback-form">
              <h2 className="font-unbounded text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Phone className="w-6 h-6 text-neon-lime" />
                Être rappelé
              </h2>
              <p className="text-zinc-400 mb-8">
                Quelques questions pour mieux préparer notre échange.
              </p>

              <form onSubmit={handleCallbackSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-white">Nom *</Label>
                    <Input
                      type="text"
                      required
                      value={callbackForm.name}
                      onChange={(e) => setCallbackForm({...callbackForm, name: e.target.value})}
                      data-testid="callback-name"
                      className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Téléphone *</Label>
                    <Input
                      type="tel"
                      required
                      placeholder="06 XX XX XX XX"
                      value={callbackForm.phone}
                      onChange={(e) => setCallbackForm({...callbackForm, phone: e.target.value})}
                      data-testid="callback-phone"
                      className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-white">Dans quel délai ? *</Label>
                    <Select
                      value={callbackForm.timeline}
                      onValueChange={(value) => setCallbackForm({...callbackForm, timeline: value})}
                    >
                      <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none">
                        <SelectValue placeholder="Délai du projet" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="immédiat">🔥 Le plus tôt possible</SelectItem>
                        <SelectItem value="1-3 mois">📅 Dans 1 à 3 mois</SelectItem>
                        <SelectItem value="3-6 mois">📆 Dans 3 à 6 mois</SelectItem>
                        <SelectItem value="+6 mois">⏳ Plus de 6 mois</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Financement *</Label>
                    <Select
                      value={callbackForm.financingType}
                      onValueChange={(value) => setCallbackForm({...callbackForm, financingType: value})}
                    >
                      <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none">
                        <SelectValue placeholder="Type de financement" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="comptant">💵 Comptant</SelectItem>
                        <SelectItem value="credit">🏦 Crédit auto</SelectItem>
                        <SelectItem value="loa">📝 LOA / LLD</SelectItem>
                        <SelectItem value="recherche">🔍 Je recherche</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 p-4 bg-zinc-800/30 border border-zinc-700">
                  <Checkbox
                    checked={callbackForm.budgetConfirmed}
                    onCheckedChange={(checked) => setCallbackForm({...callbackForm, budgetConfirmed: checked})}
                    className="border-zinc-600 data-[state=checked]:bg-neon-lime data-[state=checked]:border-neon-lime"
                  />
                  <Label className="text-white text-sm">
                    ✅ Mon budget est défini et mes fonds sont disponibles / financement validé
                  </Label>
                </div>
                
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="callback-submit"
                  className="w-full bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider py-6 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
                >
                  <span>{isSubmitting ? "Envoi..." : "Demander un rappel"}</span>
                </Button>
              </form>
            </div>
          )}

          {callbackSent && (
            <div className="mt-16 max-w-xl mx-auto bg-zinc-900/50 border border-neon-lime/50 p-8 text-center" data-testid="callback-success">
              <CheckCircle className="w-16 h-16 text-neon-lime mx-auto mb-6" />
              <h3 className="font-unbounded text-2xl font-bold text-white mb-4">
                Demande envoyée !
              </h3>
              <p className="text-zinc-400 mb-6">
                Nous vous rappellerons rapidement pour discuter de votre projet.
              </p>
              <Link to="/contact">
                <Button variant="outline" className="border-zinc-700 text-white">
                  Voir toutes les options de contact
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CalculatorPage;
